/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import SignaturePad from 'signature_pad';

@registerElement('signature-pad')
export default class SignaturePadElement extends ImpulseElement {
  /**
   * Whether the signature value is required or not.
   */
  @property({ type: Boolean }) required = false;

  @target() input: HTMLInputElement;
  @target() hiddenField?: HTMLInputElement;

  private signaturePad: SignaturePad;

  constructor() {
    super();
    this.onEndStroke = this.onEndStroke.bind(this);
  }

  connected() {
    this.calculateDimensions();
    this.canvas.style.padding = '0';
    this.signaturePad = new SignaturePad(this.canvas, { minWidth: 1, maxWidth: 1 });
    this.signaturePad.addEventListener('endStroke', this.onEndStroke);
    this.setCustomValidity(this.required);
  }

  disconnected() {
    this.clear();
    this.signaturePad.off();
    this.signaturePad.removeEventListener('endStroke', this.onEndStroke);
  }

  // Force SeptemberMornings font to load
  inputConnected() {
    if (this.ctx) {
      this.ctx.font = 'normal normal normal 4.5rem SeptemberMornings';
      this.ctx.fillText('', 0, 0);
    }
  }

  requiredChanged(value: boolean) {
    this.setCustomValidity(value);
  }

  type(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.ctx) return;

    this.ctx.font = 'normal normal normal 5.5rem SeptemberMornings';
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const xAxis = this.canvas.width / 2;
    const yAxis = this.canvas.height / 2 + 12; // Add some offset to account for text height.

    this.ctx.textAlign = 'center';
    this.ctx.fillText(input.value, xAxis, yAxis, this.canvas.width);

    if (this.input.value.length === 0) {
      this.updateHiddenField('');
    } else {
      this.updateHiddenField();
    }
  }

  clear() {
    this.signaturePad.clear();
    this.input.value = '';
    this.updateHiddenField('');
    this.emit('clear');
  }

  calculateDimensions() {
    const { width, height } = this.canvas.parentElement!.getBoundingClientRect();
    this.canvas.setAttribute('width', `${width}px`);
    this.canvas.setAttribute('height', `${height}px`);
    this.canvas.style.height = `${height}px`;
  }

  private onEndStroke() {
    this.updateHiddenField();
  }

  private updateHiddenField(value = this.signaturePad.toDataURL()) {
    this.setCustomValidity(this.required && !value);
    this.emit('signed', { detail: { value } });
    if (this.hiddenField) {
      this.hiddenField.value = value;
    }
  }

  private setCustomValidity(value: boolean) {
    this.input.setCustomValidity(value ? 'Please type or draw your signature' : '');
  }

  get ctx() {
    return this.canvas.getContext('2d');
  }

  get canvas() {
    return this.querySelector('canvas')!;
  }
}
